import { CreditCard, type LucideIcon, Wallet } from "lucide-react";

import {
  BrandingAssetType,
  CardPaymentMethodType,
  PaymentProvider,
} from "@/api-v3";

/**
 * Card heading shown only when the backend sends no `title`. Deliberately
 * hardcoded and not localized (product decision): the localized title comes
 * from PPS, and this is a last resort until it is always present.
 */
export const FALLBACK_CARD_TITLE = "Card";

/** "<kind> ending in {{0}}" keys, interpolated with the card's last four digits. */
const descriptionKeys: Record<CardPaymentMethodType, string> = {
  [CardPaymentMethodType.vc]: "claimDetail_vccDescription",
  [CardPaymentMethodType.pc]: "claimDetail_physicalCardDescription",
  [CardPaymentMethodType.ec]: "claimDetail_virtualSpendCardDescription",
  [CardPaymentMethodType.pec]: "claimDetail_physicalSpendCardDescription",
  [CardPaymentMethodType.vstc]: "claimDetail_virtualStipendCardDescription",
  [CardPaymentMethodType.pstc]: "claimDetail_physicalStipendCardDescription",
  [CardPaymentMethodType.vspc]: "claimDetail_virtualSpendCardDescription",
  [CardPaymentMethodType.pspc]: "claimDetail_physicalSpendCardDescription",
};

// Stipend Cards receive payouts, so they share the credit card icon with
// Virtual and Physical Card. Spend Cards are funded by the study, so they get
// the wallet icon on both Highnote (ec/pec) and Stripe (vspc/pspc).
const icons: Record<CardPaymentMethodType, LucideIcon> = {
  [CardPaymentMethodType.vc]: CreditCard,
  [CardPaymentMethodType.pc]: CreditCard,
  [CardPaymentMethodType.ec]: Wallet,
  [CardPaymentMethodType.pec]: Wallet,
  [CardPaymentMethodType.vstc]: CreditCard,
  [CardPaymentMethodType.pstc]: CreditCard,
  [CardPaymentMethodType.vspc]: Wallet,
  [CardPaymentMethodType.pspc]: Wallet,
};

// Stripe asked for its card image to name the card (SPEND CARD, not DEBIT), so
// Stripe cards use their own branding image instead of the Highnote one.
const cardArtwork: Record<PaymentProvider, BrandingAssetType> = {
  [PaymentProvider.highnote]: BrandingAssetType.card,
  [PaymentProvider.stripe]: BrandingAssetType["spend-card"],
};

export const getCardDescriptionKey = (
  cardType: CardPaymentMethodType
): string => descriptionKeys[cardType];

export const getCardIcon = (cardType: CardPaymentMethodType): LucideIcon =>
  icons[cardType];

export const getCardArtworkAssetType = (
  provider: PaymentProvider
): BrandingAssetType => cardArtwork[provider];
